import React, { useEffect } from 'react';
import useDigitalZoom from '../hooks/useDigitalZoom';

/**
 * ZoomableContainer — wraps video/canvas/img content with digital zoom + drag-to-pan.
 *
 * Desktop: click-drag to draw selection box, release to zoom. When zoomed, drag to pan.
 *          Right-click or left-click (without drag) to reset.
 * Mobile: pinch-to-zoom. Tap to reset.
 *
 * Props:
 *   isEview: boolean — true for eView green selection box, false for dView blue
 *   style: object — additional styles for the outer container
 *   className: string — CSS class for the outer container
 *   children: ReactNode — video/canvas/img content to make zoomable
 *   onDoubleClick: function — passed through (existing double-click behavior)
 *   onContextMenu: function — called when not zoomed (existing context menu)
 *   onZoomChange: function — called with { isZoomed, zoomState, resetZoom } on zoom state change
 */
const ZoomableContainer = ({ isEview, disabled, style, className, children, onDoubleClick, onDoubleClickWhenZoomed = false, onContextMenu: parentContextMenu, onZoomChange, ...rest }) => {
  const { containerRef, zoomState, isZoomed, selectionStyle, resetZoom, handlers, zoomStyle } = useDigitalZoom({ isEview });

  // Notify parent of zoom state changes
  const onZoomChangeRef = React.useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;
  useEffect(() => {
    if (onZoomChangeRef.current) onZoomChangeRef.current({ isZoomed, zoomState, resetZoom });
  }, [isZoomed, zoomState, resetZoom]);

  const handleContextMenu = (e) => {
    handlers.onContextMenu(e);
    if (!isZoomed && parentContextMenu) {
      parentContextMenu(e);
    }
  };

  const handleDoubleClick = (e) => {
    if (isZoomed && !onDoubleClickWhenZoomed) return;
    if (onDoubleClick) onDoubleClick(e);
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        ...style,
        overflow: 'hidden',
        userSelect: 'none',
        WebkitUserDrag: 'none',
        cursor: disabled ? 'default' : isZoomed ? (handlers.isPanning ? 'grabbing' : 'grab') : 'crosshair'
      }}
      className={`${className || ''}${isZoomed ? ' zoomable--zoomed' : ''}`}
      onMouseDown={disabled ? undefined : handlers.onMouseDown}
      onMouseMove={disabled ? undefined : handlers.onMouseMove}
      onMouseUp={disabled ? undefined : handlers.onMouseUp}
      onMouseLeave={disabled ? undefined : handlers.onMouseUp}
      onClick={disabled ? undefined : handlers.onClick}
      onContextMenu={disabled ? parentContextMenu : handleContextMenu}
      onDoubleClick={disabled ? onDoubleClick : handleDoubleClick}
      onTouchStart={disabled ? undefined : handlers.onTouchStart}
      onTouchMove={disabled ? undefined : handlers.onTouchMove}
      onTouchEnd={disabled ? undefined : handlers.onTouchEnd}
      {...rest}
    >
      <div style={zoomStyle}>
        {children}
      </div>
      {selectionStyle && <div style={selectionStyle} />}
      {isZoomed && !disabled && (
        <button
          onClick={(e) => { e.stopPropagation(); resetZoom(); }}
          style={{
            position: 'absolute', top: 6, left: 6, zIndex: 60,
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', borderRadius: 12, border: 'none',
            background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.9)',
            fontSize: 11, fontWeight: 600, cursor: 'pointer'
          }}
          title="Exit Zoom"
        >
          <span>{zoomState ? zoomState.scale + 'x' : ''}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default ZoomableContainer;
